'use client';

import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import { ResponsiveOverlay } from '@components/mobile/overlays/responsive-overlay';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import { Textarea } from '@components/ui/textarea';
import { makeId } from '@db/makeId';
import { ANNOUNCEMENT_TYPE } from '@ent/entity-type';
import { useZero } from '@rocicorp/zero/react';
import { useTranslations } from 'next-intl';
import { FC, FormEvent, useState } from 'react';
import { runZeroMutation } from '../zero/run-mutation';

type CreateAnnouncementDialogProps = {
  organization_id: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (announcementId: string) => void;
};

const CreateAnnouncementDialog: FC<CreateAnnouncementDialogProps> = ({
  organization_id,
  open,
  onOpenChange,
  onCreated
}) => {
  const t = useTranslations();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const z = useZero() as any;
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);
  const [publishAt, setPublishAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTitle('');
    setBody('');
    setPinned(false);
    setPublishAt('');
    setExpiresAt('');
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    if (!trimmedTitle || !trimmedBody || submitting) return;

    setSubmitting(true);
    const id = makeId(ANNOUNCEMENT_TYPE);
    const result = await runZeroMutation(
      z.mutate.announcements.create({
        id,
        organizationId: organization_id,
        title: trimmedTitle,
        body: trimmedBody,
        pinned,
        publishAt: publishAt ? new Date(publishAt).getTime() : undefined,
        expiresAt: expiresAt ? new Date(expiresAt).getTime() : undefined,
        now: Date.now()
      }),
      { errorTitle: 'Could not post announcement' }
    );
    setSubmitting(false);

    if (result) {
      reset();
      onOpenChange(false);
      onCreated?.(id);
    }
  };

  return (
    <ResponsiveOverlay
      open={open}
      onOpenChange={handleOpenChange}
      title={t('common.create-announcement')}
      description={t('common.create-announcement-description')}
    >
      <form onSubmit={handleSubmit}>
        <div className="py-4 flex flex-col gap-4">
          <div>
            <Label htmlFor="announcement-title">
              {t('common.announcement-title')}
            </Label>
            <Input
              id="announcement-title"
              autoFocus
              value={title}
              onChange={event => setTitle(event.target.value)}
              placeholder={t('common.announcement-title')}
            />
          </div>
          <div>
            <Label htmlFor="announcement-body">{t('common.message')}</Label>
            <Textarea
              id="announcement-body"
              value={body}
              onChange={event => setBody(event.target.value)}
              placeholder={t('common.message')}
              rows={5}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="announcement-pinned"
              checked={pinned}
              onCheckedChange={checked => setPinned(checked === true)}
            />
            <Label htmlFor="announcement-pinned">{t('common.pin')}</Label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="announcement-publish-at">
                {t('common.publish-at')}
              </Label>
              <Input
                id="announcement-publish-at"
                type="datetime-local"
                value={publishAt}
                onChange={event => setPublishAt(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="announcement-expires-at">
                {t('common.expires-at')}
              </Label>
              <Input
                id="announcement-expires-at"
                type="datetime-local"
                value={expiresAt}
                onChange={event => setExpiresAt(event.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={!title.trim() || !body.trim() || submitting}
          >
            {t('common.create-announcement')}
          </Button>
        </div>
      </form>
    </ResponsiveOverlay>
  );
};

export default CreateAnnouncementDialog;
